import { Fragment, useCallback, useMemo } from 'react';
import isEmpty from 'lodash/isEmpty';
import flatMap from 'lodash/flatMap';

import { PrimaryNavTabTooltipItem } from '../../PrimaryNavTooltipMenuWithSectionsTab.types';
import { I18nText } from '../../../../../../../../types';
import { IconsEnum } from '../../../../../../../../assets/icons/types';
import { PrimaryNavTabsEnum } from '../../../../../../../hooks/useMenu/types';
import { TeamNanoID } from '../../../../../../../../main/teams/teamsTypes';

import { Icon } from '../../../../../../../../helpers/Icon';
import { LocalForage } from '../../../../../../../../utils/LocalForage';
import { NextLinkHelper } from '../../../../../../../../helpers/links/NextLinkHelper';
import { Translate } from '../../../../../../../../helpers/Translate';
import { CheckPermissions } from '../../../../../../../../helpers/CheckPermissions';

import { getNavMenuTabPath } from '../../../../../../../utils/getNavMenuTabPath';

interface PrimaryNavTooltipMenuWithSectionsTabSectionProps {
  companyNanoId?: TeamNanoID;
  tab: PrimaryNavTabsEnum;
  tooltipI18nText: I18nText;
  tooltipLinks?: PrimaryNavTabTooltipItem[];
  asPath: string;
}

function PrimaryNavTooltipMenuWithSectionsTabSection({
  asPath,
  companyNanoId,
  tab,
  tooltipI18nText,
  tooltipLinks
}: PrimaryNavTooltipMenuWithSectionsTabSectionProps) {
  const handleClickToLink = useCallback<(linkHref: string) => void>(
    (linkHref) =>
      LocalForage.setItem(getNavMenuTabPath(tab, companyNanoId), linkHref),
    [companyNanoId, tab]
  );

  const allLinksPermissions = useMemo(
    () => flatMap(tooltipLinks, (link) => link.permissions),
    [tooltipLinks]
  );

  if (isEmpty(tooltipLinks)) {
    return null;
  }

  return (
    <CheckPermissions actions={allLinksPermissions}>
      <div className="px-2 pt-2 pb-1">
        <div className="px-2 text-gray-400 dark:text-gray-400 text-xs leading-5">
          <Translate id={tooltipI18nText} />
        </div>
        <hr className="flex-1 dark:border-gray-600" />
      </div>

      {tooltipLinks.map((link) => (
        <Fragment key={link.name}>
          <CheckPermissions actions={link.permissions}>
            <NextLinkHelper
              className="flex dark:hover:bg-gray-800 hover:bg-gray-100 px-3 3xl:px-4 py-1.5 3xl:py-2 text-xs 3xl:text-sm w-full whitespace-nowrap"
              href={link.href}
              onClick={() => handleClickToLink(link.href)}
            >
              <span className="mr-auto">
                <Translate id={link.i18nText} />
              </span>

              {(link.activeTest?.(asPath) ||
                asPath.startsWith(`/${link.name}`)) && (
                <span className="text-blue-600 pl-1.5">
                  <Icon icon={IconsEnum.CHECK} className="h-5 w-5" />
                </span>
              )}
            </NextLinkHelper>
          </CheckPermissions>
        </Fragment>
      ))}
    </CheckPermissions>
  );
}

export default PrimaryNavTooltipMenuWithSectionsTabSection;
